import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { getFirebaseAuth } from '../config/firebase';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { InactivityWarningDialog } from '../components/InactivityWarningDialog';

/**
 * Authentication context value interface
 */
interface AuthContextValue {
  /** Current authenticated user, null if not authenticated, undefined during initial load */
  user: User | null | undefined;
  /** Whether the authentication state is being initialized */
  loading: boolean;
  /** Firebase ID token for API calls, null if not authenticated */
  idToken: string | null;
  /** Partner ID from Firebase custom claims */
  partnerId: number | null;
  /** User roles from Firebase custom claims */
  roles: string[];
  /** Check if the user has a specific role */
  hasRole: (role: string) => boolean;
  /** Sign out the current user */
  signOut: () => Promise<void>;
  /** Refresh the ID token */
  refreshToken: () => Promise<string | null>;
}

/**
 * Authentication context
 *
 * Provides global authentication state and user information
 * throughout the application
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Authentication provider props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Manages authentication state and provides it to child components
 * Automatically listens for Firebase auth state changes
 * Handles token refresh and storage
 *
 * Usage:
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 */
export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Get and store ID token, extract partnerId from custom claims
   */
  const updateIdToken = async (currentUser: User | null): Promise<string | null> => {
    if (!currentUser) {
      setIdToken(null);
      setPartnerId(null);
      setRoles([]);
      localStorage.removeItem('authToken');
      return null;
    }

    try {
      const tokenResult = await currentUser.getIdTokenResult();
      const token = tokenResult.token;
      setIdToken(token);
      localStorage.setItem('authToken', token);

      // Extract partnerId and roles from custom claims
      const claims = tokenResult.claims;
      if (claims.partnerId != null) {
        setPartnerId(Number(claims.partnerId));
      } else {
        setPartnerId(null);
      }
      setRoles(Array.isArray(claims.roles) ? (claims.roles as string[]) : []);

      // Support both "role" (string) and "roles" (array) claim formats
      if (Array.isArray(claims.roles)) {
        setRoles(claims.roles as string[]);
      } else if (typeof claims.role === 'string') {
        setRoles([claims.role]);
      } else {
        setRoles([]);
      }

      return token;
    } catch (error) {
      console.error('[AuthContext] Failed to get ID token:', error);
      setIdToken(null);
      setPartnerId(null);
      setRoles([]);
      localStorage.removeItem('authToken');
      return null;
    }
  };

  /**
   * Refresh the current user's ID token
   */
  const refreshToken = async (): Promise<string | null> => {
    if (!user) {
      return null;
    }

    try {
      const tokenResult = await user.getIdTokenResult(true); // Force refresh
      setIdToken(tokenResult.token);
      localStorage.setItem('authToken', tokenResult.token);

      if (tokenResult.claims.partnerId != null) {
        setPartnerId(Number(tokenResult.claims.partnerId));
      }
      setRoles(
        Array.isArray(tokenResult.claims.roles) ? (tokenResult.claims.roles as string[]) : []
      );

      if (Array.isArray(tokenResult.claims.roles)) {
        setRoles(tokenResult.claims.roles as string[]);
      } else if (typeof tokenResult.claims.role === 'string') {
        setRoles([tokenResult.claims.role]);
      }

      return tokenResult.token;
    } catch (error) {
      console.error('[AuthContext] Failed to refresh token:', error);
      return null;
    }
  };

  /**
   * Sign out the current user
   */
  const signOut = async (): Promise<void> => {
    try {
      const auth = getFirebaseAuth();
      await firebaseSignOut(auth);
      setUser(null);
      setIdToken(null);
      setPartnerId(null);
      setRoles([]);
      localStorage.removeItem('authToken');
    } catch (error) {
      console.error('[AuthContext] Sign out failed:', error);
      throw error;
    }
  };

  /**
   * Listen for authentication state changes
   */
  useEffect(() => {
    const auth = getFirebaseAuth();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      await updateIdToken(currentUser);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  /**
   * Set up token refresh interval (every 50 minutes)
   * Firebase tokens expire after 1 hour
   */
  useEffect(() => {
    if (!user) {
      return;
    }

    const refreshInterval = setInterval(
      async () => {
        await refreshToken();
      },
      50 * 60 * 1000
    ); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [user]);

  /**
   * Auto-logout after 60 minutes of inactivity (SOPHIA-34816).
   * ProtectedRoute redirects to /login once the user is signed out.
   */
  const { showWarning, timeoutAt, staySignedIn } = useInactivityTimeout({
    enabled: !!user,
    onTimeout: () => {
      void signOut().catch(() => {
        // signOut already logs; the auth listener still clears local state
      });
    },
  });

  const hasRole = (role: string): boolean => roles.includes(role);

  const value: AuthContextValue = {
    user,
    loading,
    idToken,
    partnerId,
    roles,
    hasRole,
    signOut,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <InactivityWarningDialog
        open={showWarning}
        timeoutAt={timeoutAt}
        onStaySignedIn={staySignedIn}
        onSignOut={() => void signOut().catch(() => undefined)}
      />
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication context
 *
 * Usage:
 *   const { user, loading, idToken, signOut } = useAuth();
 *
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
